<template>
  <div class="checkout-complete">
    <Heading color="blue" size="large" tag="h1">{{ headline }}</Heading>

    <Divider class="my-4 xl:my-6" />

    <div class="my-4 grid gap-6 xl:grid-cols-3 xl:gap-8">
      <AccountAddressCard
        v-if="userDefaultBillingAddress"
        :key="userDefaultBillingAddress?.id"
        :address="userDefaultBillingAddress"
        is-billing
      >
        <template #title>
          <CommonLabel path="BWF/shop/shared/billingAddressLabel" />
        </template>
      </AccountAddressCard>

      <Divider class="xl:hidden" />

      <AccountAddressCard
        :key="userDefaultShippingAddress?.id"
        :address="userDefaultShippingAddress"
        is-shipping
      >
        <template #title>
          <CommonLabel path="BWF/shop/shared/shippingAddressLabel" />
        </template>
      </AccountAddressCard>

      <div v-if="isGuestSession" class="relative flex flex-col gap-4">
        <Heading size="medium" tag="h3">
          <CommonLabel path="BWF/shop/checkout/addressForm/emailAddressLabel" />
        </Heading>
        <span> {{ user?.email }} </span>
        <button
          class="text-icon-link text-icon-link absolute right-0 top-0 xl:relative xl:right-auto xl:top-auto"
          @click.prevent="emailModal.open()"
        >
          <CommonLabel
            class="hidden xl:block xl:place-self-start"
            path="BWF/shop/checkout/addressForm/emailAddressChangeLabel"
          />
          <CommonLabel class="xl:hidden" path="BWF/shop/shared/change" />

          <SvgIcon class="h-4 w-4" name="help-circle-outline" />
        </button>
        <ModalComponent :controller="emailModal">
          <template #headline>
            <CommonLabel path="BWF/shop/checkout/addressForm/emailAddressLabel" />
          </template>
          <form @submit.prevent="emailEditSave">
            <InputField
              v-model="emailState.email"
              :error-message="emailVuelidate?.$errors[0]?.$message"
              :label="checkoutLabels?.emailAddressLabel"
              :placeholder="checkoutLabels?.emailAddressPlaceholder"
              autocomplete="off"
              class="form-field-grey mb-8"
              name="email-address"
              required="required"
              type="email"
              @change="emailVuelidate?.$touch()"
            />
            <button class="btn btn-blue" type="submit">
              <CommonLabel path="BWF/shop/shared/save" />
            </button>
          </form>
        </ModalComponent>
      </div>

      <Divider class="col-span-full" />

      <div
        class="col-span-full flex flex-col gap-4 xl:col-span-2"
        :class="{ 'pointer-events-none animate-pulse grayscale': isLoading['paymentMethods'] }"
      >
        <Heading size="medium" tag="h3">
          <CommonLabel path="BWF/shop/checkout/complete/paymentMethod" />
        </Heading>
        <PaymentMethodSelection
          ref="paymentMethodSelection"
          v-model:selected-payment-method="selectedPaymentMethod"
          :payment-methods="paymentMethods"
        />
      </div>

      <Divider class="col-span-full" />

      <fieldset class="contents">
        <legend class="col-span-full">
          <Heading size="medium" tag="h3">
            <CommonLabel path="BWF/shop/checkout/complete/shippingMethod" />
          </Heading>
        </legend>
        <div
          v-for="shippingOption in shippingMethods"
          :key="shippingOption.id"
          :class="{ 'pointer-events-none animate-pulse grayscale': isLoading['paymentMethods'] }"
          class="flex items-center"
        >
          <RadioGroup v-model="selectedShippingMethod" name="shipping-method">
            <Radio :value="shippingOption.id">
              <div v-if="shippingOption?.deliveryTime?.name" class="flex flex-col gap-1">
                {{ shippingOption.name }}
                <Tint class="text-sm">
                  <CommonLabel path="BWF/shop/checkout/complete/shippingTime" />
                  {{ shippingOption?.deliveryTime?.name }}
                </Tint>
              </div>
              <template v-else>{{ shippingOption.name }}</template>
            </Radio>
          </RadioGroup>
        </div>
      </fieldset>

      <Divider class="col-span-full mb-4 hidden xl:block" />
    </div>

    <div class="my-4 grid gap-4 xl:grid-cols-3 xl:gap-8">
      <div class="flex flex-col gap-4 xl:col-span-2">
        <ul>
          <li
            v-for="(cartItem, index) in getLineItemsWithoutPromotion(cartItems)"
            :key="cartItem.id"
          >
            <CheckoutCartItem :cart-item="cartItem" :column-names="index === 0" />
            <Divider
              v-if="index < getLineItemsWithoutPromotion(cartItems).length - 1"
              class="my-4 xl:my-6"
            />
          </li>
        </ul>

        <CommonLabel class="mt-6" path="BWF/shared/vatFootnote" />
      </div>

      <aside class="flex flex-col gap-4 xl:col-span-1">
        <CheckoutSummary />
        <Divider class="xl:hidden" />
        <!--                <CheckoutPromotionCode />-->
      </aside>
    </div>

    <Divider class="mb-4 hidden xl:block" />

    <form
      id="checkout-create-order"
      class="col-span-full grid gap-6 py-8"
      method="post"
      name="checkout-create-order"
      @submit.prevent="invokeSubmit"
    >
      <EditableArea
        v-if="isMagnoliaEdit || !newsletterAlreadyRegistered"
        :content="newsletterContent"
      >
        <CheckoutNewsletter v-model="checkoutNewsletterConsent" :content="newsletterContent" />
      </EditableArea>
      <template v-if="marketSetupAdmin?.legalCheckbox">
        <p class="hidden xl:block">
          <CommonLabel path="BWF/shop/checkout/complete/rightOfWithdrawal" />
        </p>

        <Checkbox
          v-if="marketSetupAdmin?.legalCheckbox"
          v-model="legalState.legal"
          :error="!!v$?.legal?.$errors?.[0]?.$message"
          :error-text="v$?.legal?.$errors?.[0]?.$message"
          name="legal"
          @update:model-value="v$.legal.$touch()"
        >
          <CommonLabel path="BWF/shop/checkout/complete/privacyPolicyNotice" />
        </Checkbox>
      </template>
      <template v-else>
        <CommonLabel path="BWF/shop/checkout/complete/rightOfWithdrawalNoCheckbox" />
        <CommonLabel path="BWF/shop/checkout/complete/privacyPolicyNoticeNoCheckbox" />
      </template>
    </form>

    <Divider class="col-span-full my-10 hidden xl:block" />

    <div class="grid gap-8 xl:grid-cols-3">
      <button
        :class="{ 'pointer-events-none animate-pulse': isLoading?.['placeOrder'] || false }"
        :disabled="isLoading?.['placeOrder'] || null"
        class="btn btn-blue btn-size-normal mt-4 xl:mt-0"
        form="checkout-create-order"
        type="submit"
      >
        <span> <CommonLabel path="BWF/shop/checkout/complete/confirmPurchase" /></span>
      </button>
      <div class="xl:order-first xl:col-span-2">
        <button class="text-icon-link mx-auto xl:mx-0" @click="$router.go(-1)">
          <SvgIcon class="h-3 w-3 -rotate-90" name="Arrow-Up" />
          <span>
            <CommonLabel path="BWF/shop/shared/backToShop" />
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { Divider } from "~/components/clarity-ui/Divider";
import { Heading } from "~/components/clarity-ui/Heading";
import { Radio } from "~/components/clarity-ui/Radio";
import { RadioGroup } from "~/components/clarity-ui/Radio/RadioGroup";
import { Tint } from "~/components/clarity-ui/Tint";
import AccountAddressCard from "~/templates/components/shop/account/AccountAddressCard.vue";
import { Checkbox } from "~/components/clarity-ui/Checkbox";
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import useCommonLabels from "~/composables/useCommonLabels";
import { getLineItemsWithoutPromotion } from "~/utils/helper/shop/cart";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import ModalComponent from "~/templates/components/ModalComponent.vue";
import CheckoutNewsletter from "~/templates/components/shop/checkout/CheckoutNewsletter.vue";
import InputField from "~/templates/elements/form/InputField.vue";
import PaymentMethodSelection from "~/templates/components/shop/checkout/PaymentMethodSelection.vue";
import { EditableArea } from "@magnolia/vue-editor";
const { isMagnoliaEdit } = useMagnoliaContext();
const props = defineProps(["newsletterContent"]);
const { pushShopwareError } = useShopwareNotifications();

const { marketSetupAdmin } = useMarketSetup();
const { saveOrder, getQueryString } = useOrderSession();

const { paymentMethods, shippingMethods, getPaymentMethods, getShippingMethods, createOrder } =
  useCheckout();
const { user, isGuestSession, userDefaultBillingAddress, userDefaultShippingAddress } = useUser();
const {
  selectedShippingMethod: shippingMethod,
  selectedPaymentMethod: paymentMethod,
  setShippingMethod,
  setPaymentMethod,
  refreshSessionContext,
} = useSessionContext();

const { cartItems, refreshCart } = useCart();
const { loadCustomerAddresses } = useAddress();
const { trackShippingInfo, trackPaymentInfo, trackPlaceOrder, trackOrderSummary } =
  useCheckoutTracking();

const isLoading = ref<{ [key: string]: boolean }>({});

const selectedShippingMethod = computed({
  get(): string {
    return shippingMethod.value?.id || "";
  },
  async set(shippingMethodId: string) {
    isLoading.value[shippingMethodId] = true;
    await setShippingMethod({ id: shippingMethodId });
    await refreshCart();
    isLoading.value[shippingMethodId] = false;
  },
});
const selectedPaymentMethod = computed({
  get(): string {
    return paymentMethod.value?.id || "";
  },
  async set(paymentMethodId: string) {
    isLoading.value[paymentMethodId] = true;
    await setPaymentMethod({ id: paymentMethodId });
    isLoading.value[paymentMethodId] = false;
  },
});

const emailModal = useModal();

const { getLabels, getLabelByPath } = useCommonLabels();

const headline = getLabelByPath("/BWF/shop/checkout/complete/completeOrderHeadline");
const checkoutLabels = getLabels("/BWF/shop/checkout/addressForm/", [
  "emailAddressPlaceholder",
  "emailAddressLabel",
]);

watch(cartItems, async () => {
  isLoading.value["shippingMethods"] = true;
  isLoading.value["paymentMethods"] = true;

  await Promise.all([
    getShippingMethods({ forceReload: true }),
    getPaymentMethods({ forceReload: true }),
  ]);

  isLoading.value["shippingMethods"] = false;
  isLoading.value["paymentMethods"] = false;
});

watch(shippingMethods, async () => {
  const hasShippingMethod = !!shippingMethods.value.find(
    (method) => method.id === selectedShippingMethod.value,
  );
  if (!hasShippingMethod) {
    // the current shipping method is not available fallback to the first available
    await setShippingMethod({ id: shippingMethods.value[0]?.id });
  }
});

watch(paymentMethods, async () => {
  const hasPaymentMethod = !!paymentMethods.value?.find(
    (method) => method.id === selectedPaymentMethod.value,
  );
  if (!hasPaymentMethod) {
    // current payment method not available fallback to the first available
    await setPaymentMethod({ id: paymentMethods.value[0]?.id });
  }
});

const { emailValidators, requiredValidator, consentValidators } = await useValidation();

const { apiInstance } = useShopwareContext();

const legalRules = computed(() => ({
  legal: {
    ...consentValidators,
  },
}));

// set legal straight to true if it's not required
const legalState = reactive({
  legal: !marketSetupAdmin.value.legalCheckbox,
});
const v$ = useVuelidate(legalRules, legalState);

const emit = defineEmits<{
  (e: "loadingStart"): void;
  (e: "loadingEnd"): void;
  (e: "success"): void;
}>();

const emailState = reactive({
  email: user?.value?.email ?? "",
});

const emailRules = computed(() => ({
  email: {
    ...requiredValidator,
    ...emailValidators,
  },
}));

const emailVuelidate = useVuelidate(emailRules, emailState);

const emailEditSave = async (): Promise<void> => {
  emailVuelidate.value.$touch();
  if (emailState?.email !== user?.value?.email) {
    try {
      await apiInstance.invoke.post(`/store-api/account/guest-customer/change-email`, {
        email: emailState?.email,
      });
      await refreshSessionContext();
      await evaluateDoiState(user.value?.email);
      emit("success");
      emailModal.close();
    } catch (error) {
      console.error("error save email", error);
      emailModal.close();
    }
  } else {
    emailModal.close();
  }
};

const { register, getDoiState } = useBritaMiddleware();
const checkoutNewsletterConsent = ref(false);
const newsletterAlreadyRegistered = ref(false);

const evaluateDoiState = async (email: string) => {
  const doiData = await getDoiState(email);
  if (doiData?.donotbulkemail === false || doiData?.mailDoiState === "CONFIRMED") {
    newsletterAlreadyRegistered.value = true;
    checkoutNewsletterConsent.value = false;
  } else {
    newsletterAlreadyRegistered.value = false;
  }
};

const paymentMethodSelection = ref(null);
const invokeSubmit = async () => {
  v$.value.$touch();
  // if rendered, mollie components need to be valid as well
  const paymentValid = (await paymentMethodSelection.value?.validate()) ?? true;
  const formValid = await v$.value.$validate();
  const valid = formValid && paymentValid;

  if (valid) {
    try {
      isLoading.value["placeOrder"] = true;
      const {
        public: { baseUrl },
      } = useRuntimeConfig();

      trackShippingInfo();
      trackPaymentInfo();
      trackPlaceOrder(selectedPaymentMethod?.value);
      const order = await createOrder();
      // save order to session
      saveOrder(order);
      const cmsSuccessUrl = `${marketSetupAdmin.value?.checkoutSuccessPage}/${order.id}`;
      const cmsPaymentUrl = `${marketSetupAdmin.value?.orderPaymentPage}/${order.id}`;
      const queryString = isGuestSession.value ? `?${getQueryString()}` : "";
      const paymentData = {
        orderId: order.id,
        finishUrl: `${baseUrl}${cmsSuccessUrl}${queryString}`,
        errorUrl: `${baseUrl}${cmsPaymentUrl}${queryString}`,
      };

      if (checkoutNewsletterConsent.value) {
        try {
          register(
            user?.value?.email,
            props.newsletterContent?.interactionId,
            props.newsletterContent?.actionId,
          ).then(() => {
            console.log("success newsletter registration");
          });
        } catch (e) {
          console.error("error newsletter registration");
        }
      }

      const { data } = await apiInstance.invoke.post("/store-api/handle-payment", paymentData);
      // refresh cart to be sure that its empty
      await refreshCart();
      if (data.redirectUrl) {
        await navigateTo(data.redirectUrl, { external: true });
        return;
      }
      return navigateTo(cmsSuccessUrl, { external: true });
    } catch (e) {
      isLoading.value["placeOrder"] = false;
      await pushShopwareError(e);
      if (e.messages.find((message) => message.code === "CHECKOUT__CART_INVALID")) {
        await refreshCart();
      }
    }
  } else {
    // error !?
    console.error("form is not valid");
  }
};

onMounted(async () => {
  // id user is authenticated
  emit("loadingStart");

  isLoading.value["shippingAddress"] = true;
  isLoading.value["shippingMethods"] = true;
  isLoading.value["paymentMethods"] = true;

  await evaluateDoiState(user.value?.email);

  try {
    await Promise.all([loadCustomerAddresses(), getShippingMethods(), getPaymentMethods()]);
  } catch (e) {
    console.error("Fehler bei der initialisierung der Order ", e);
  }

  isLoading.value["shippingAddress"] = false;
  isLoading.value["shippingMethods"] = false;
  isLoading.value["paymentMethods"] = false;
  trackOrderSummary();
  emit("loadingEnd");
});
</script>
